'use client';

import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faCopy } from '@fortawesome/pro-solid-svg-icons/faCopy';
import { faCamera } from '@fortawesome/pro-solid-svg-icons/faCamera';
import { faPencil } from '@fortawesome/pro-solid-svg-icons/faPencil';
import { faFilm } from '@fortawesome/pro-solid-svg-icons/faFilm';

import { useContentSize } from '@osmosis/nextjs-components/context/ContentSizeContext';

import { useUserContext } from '../../../context/UserContext';
import { usePagesContext } from '../../../context/PagesProvider';

interface Props {
  title: string;
  learnNodeID: number;
}

const COMMON_BUTTON_CLASS =
  '!px-2 text-sm font-bold hover:bg-blue-100 border-0 hover:text-darkest-blue-500';

/**
 * Admin tools bar.
 */
const AdminToolbar = ({ title, learnNodeID }: Props): JSX.Element | null => {
  const intl = useIntl();
  const { user } = useUserContext();
  const { params } = usePagesContext();
  const { getClassValue } = useContentSize();

  const labelVisibility = getClassValue({
    defaultClasses: 'hidden md:inline',
    breakpoints: { md: 'inline', base: 'hidden' },
  });

  const iconButtonClass = getClassValue({
    defaultClasses: COMMON_BUTTON_CLASS,
    breakpoints: {
      md: COMMON_BUTTON_CLASS,
      base: `${COMMON_BUTTON_CLASS} [&>div:first-child]:mr-0`,
    },
  });

  const isInternal = !!Object.keys(user?.admin || {}).length;
  const isEditor = !!user?.admin?.editor;

  const [copied, setCopied] = useState(false);

  if (!isInternal && !isEditor) return null;

  const editChaptersHref = `/editchapters/${params?.concept}`;

  const ADMIN_TOOLTIP_ID = 'v2-admin-tooltip';

  const copyTooltip = copied
    ? intl.formatMessage({
        id: 'learn.v2.admin_copy_node.copied',
        defaultMessage: 'Copied!',
      })
    : intl.formatMessage(
        {
          id: 'learn.v2.admin_copy_node.tooltip',
          defaultMessage: 'Copy learn node ID ({learnNodeID}) to clipboard',
        },
        { learnNodeID },
      );

  const copyNodeId = async () => {
    try {
      await navigator.clipboard.writeText(learnNodeID.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable (insecure context) */
    }
  };

  const openEditLearn = () => {
    window.open(`/editlearn?title=${encodeURIComponent(title)}`);
  };

  return (
    <Flex
      data-testid="v2-admin-toolbar"
      className={`flex-wrap items-center gap-2`}
    >
      <P className={cn('mr-2 text-sm font-bold py-2')}>
        {intl.formatMessage({
          id: 'learn.v2.admin_tools',
          defaultMessage: 'Admin tools:',
        })}
      </P>

      {isInternal && (
        <Button
          variant="transparent"
          color="default"
          data-testid="v2-admin-copy-node"
          data-tooltip-id={ADMIN_TOOLTIP_ID}
          data-tooltip-content={copyTooltip}
          startIcon={<Icon icon={copied ? faCheck : faCopy} size={16} />}
          onClick={copyNodeId}
          className={iconButtonClass}
        >
          <span className={labelVisibility}>
            {intl.formatMessage({
              id: 'learn.v2.admin_copy_node',
              defaultMessage: 'Copy Learn node',
            })}
          </span>
        </Button>
      )}

      {isEditor && (
        <>
          <Button
            as="a"
            href={editChaptersHref}
            variant="transparent"
            color="default"
            data-testid="v2-admin-screenshot"
            data-tooltip-id={ADMIN_TOOLTIP_ID}
            data-tooltip-content={intl.formatMessage({
              id: 'learn.v2.admin_screenshot.tooltip',
              defaultMessage: 'Open the screenshot editor',
            })}
            startIcon={<Icon icon={faCamera} size={16} />}
            className={iconButtonClass}
          >
            <span className={labelVisibility}>
              {intl.formatMessage({
                id: 'learn.v2.admin_screenshot',
                defaultMessage: 'Screenshot',
              })}
            </span>
          </Button>

          <Button
            variant="transparent"
            color="default"
            data-testid="v2-admin-edit-learn"
            data-tooltip-id={ADMIN_TOOLTIP_ID}
            data-tooltip-content={intl.formatMessage({
              id: 'learn.v2.admin_edit_learn.tooltip',
              defaultMessage: 'Edit this learn page',
            })}
            startIcon={<Icon icon={faPencil} size={16} />}
            onClick={openEditLearn}
            className={iconButtonClass}
          >
            <span className={labelVisibility}>
              {intl.formatMessage({
                id: 'learn.v2.admin_edit_learn',
                defaultMessage: 'Edit Learn',
              })}
            </span>
          </Button>

          <Button
            as="a"
            href={editChaptersHref}
            variant="transparent"
            color="default"
            data-testid="v2-admin-edit-chapter"
            data-tooltip-id={ADMIN_TOOLTIP_ID}
            data-tooltip-content={intl.formatMessage({
              id: 'learn.v2.admin_edit_chapter.tooltip',
              defaultMessage: 'Edit the chapters for this video',
            })}
            startIcon={<Icon icon={faFilm} size={16} />}
            className={iconButtonClass}
          >
            <span className={labelVisibility}>
              {intl.formatMessage({
                id: 'learn.v2.admin_edit_chapter',
                defaultMessage: 'Edit Chapter',
              })}
            </span>
          </Button>
        </>
      )}

      <Tooltip id={ADMIN_TOOLTIP_ID} place="bottom" />
    </Flex>
  );
};

export default AdminToolbar;
